import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../contexts/AuthContext";
import CustomButton from "../ui/CustomButton";

const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  minHeight: "100vh",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  paddingTop: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2.25),
  width: "100%",
  maxWidth: 400,
  borderRadius: theme.spacing(4),
  textAlign: "center",
  backgroundColor: theme.palette.background.default,
  boxShadow: "none",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1.5),
    backgroundColor: "transparent",
    "& .MuiOutlinedInput-input": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiInputLabel-root": {
    backgroundColor: "transparent",
  },
}));

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailRegex.test(formData.email)) {
        errors.email =
          "Please enter a valid email address (e.g., name@gmail.com)";
      }
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters long";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const result = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
      });
      if (result.success) {
        navigate(returnUrl || "/");
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("An error occurred during registration");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BackgroundBox>
      <StyledPaper elevation={0}>
        <Typography variant="h5" fontWeight="500" sx={{ mb: 2 }}>
          Create Account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <StyledTextField
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            fullWidth
            autoFocus
            placeholder="First Name"
            error={!!fieldErrors.firstName}
            helperText={fieldErrors.firstName}
          />

          <StyledTextField
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            fullWidth
            placeholder="Last Name"
            error={!!fieldErrors.lastName}
            helperText={fieldErrors.lastName}
          />

          <StyledTextField
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            fullWidth
            placeholder="Email"
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
          />

          <StyledTextField
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            fullWidth
            placeholder="Password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={!!fieldErrors.password}
            helperText={
              fieldErrors.password || "Password must be at least 6 characters"
            }
          />

          <StyledTextField
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            fullWidth
            placeholder="Confirm Password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleConfirmPasswordVisibility}
                    edge="end"
                    aria-label="toggle confirm password visibility"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={!!fieldErrors.confirmPassword}
            helperText={fieldErrors.confirmPassword}
          />

          <CustomButton
            fullWidth
            variant="contained"
            type="submit"
            size="large"
            disabled={loading}
            preventSpam={true}
            spamDelay={2000}
            sx={{
              height: "50px",
              borderRadius: "25px",
              textTransform: "none",
              mt: 2,
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Create Account"
            )}
          </CustomButton>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{" "}
              <Box
                component="span"
                sx={{
                  color: "black",
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate(
                    `/login${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ""}`,
                  )
                }
              >
                Sign in
              </Box>
            </Typography>
          </Box>
        </form>
      </StyledPaper>
    </BackgroundBox>
  );
};

export default Signup;
